import React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import {
    getTimezoneNames,
    getLocalizedName,
} from "@impero-com/common/utils/timezone";
import i18next from "i18next";
import memoizeOne from "memoize-one";

import { RawMultiSelectInput } from "./multiSelectInput";
import { SelectInputOption } from "./selectInput";

interface CommonProps {
    name: string;
    values: Array<string>;
    className?: string;
}

interface RawMultiTimezoneSelectReadOnlyProps extends CommonProps {
    display: "Text" | "Input";
}

interface RawMultiTimezoneSelectEditProps extends CommonProps {
    onChange: (newTimezone: string) => void;
    showSearch?: boolean;
}

type RawMultiTimezoneSelectProps =
    | ({ type: "READ_ONLY" } & RawMultiTimezoneSelectReadOnlyProps)
    | ({ type: "EDIT" } & RawMultiTimezoneSelectEditProps);

class RawMultiTimezoneSelect extends React.PureComponent<RawMultiTimezoneSelectProps> {
    render(): React.ReactNode {
        const { props } = this;
        const options = prepareOptions(i18next.language);

        switch (props.type) {
            case "READ_ONLY": {
                const { name, values, display, className } = props;
                return (
                    <RawMultiSelectInput
                        type="READ_ONLY"
                        name={name}
                        values={values}
                        options={options}
                        display={display}
                        className={className}
                    />
                );
            }
            case "EDIT": {
                const { name, values, onChange, className, showSearch } = props;
                const onChangeInternal = (value: string | undefined) => {
                    if (value) {
                        onChange(value);
                    }
                };

                return (
                    <RawMultiSelectInput
                        type="EDIT"
                        name={name}
                        values={values}
                        options={options}
                        onChange={onChangeInternal}
                        className={className}
                        showSearch={showSearch}
                    />
                );
            }
            default: {
                return assertNever(props);
            }
        }
    }
}

const prepareOptions = memoizeOne(
    (_language: string): Array<SelectInputOption> => {
        const tzNames = getTimezoneNames();
        return tzNames.map((value) => {
            return { value, label: getLocalizedName(value) };
        });
    }
);

export { RawMultiTimezoneSelect, type RawMultiTimezoneSelectProps };
